/**
 * Outstanding Invoices — kanban-driven AR view.
 *
 * Columns: Open · Due Soon (≤7d) · Overdue · Paid (last 30d).
 * Each card shows the invoice number, customer, $ total, dual ageing
 * chips (Net-N driven bucket + classic 0/30/60/90 colour), and quick
 * actions (View PDF · Mark paid · Mark unpaid). Mark-paid opens a
 * minimal dialog with date + optional note (bank ref / cheque #).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Receipt, FileText, Check, ArrowsCounterClockwise, CurrencyDollar, Clock, WarningCircle, Calendar, EnvelopeSimple, Bell, BellSlash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { toast } from 'sonner';
import {
  getOutstandingKanban,
  markInvoicePaid,
  markInvoiceUnpaid,
  invoicePdfUrl,
  sendInvoiceReminder,
  getInvoiceReminders,
  setInvoiceSkipAutoReminders,
} from '../lib/api';
import ArEffectivenessStrip from '../components/ArEffectivenessStrip';

const COLUMN_DEFS = [
  { key: 'open',         label: 'Open',         icon: Receipt,        accent: 'border-zinc-300  text-zinc-700  bg-zinc-50' },
  { key: 'due_soon',     label: 'Due Soon (≤7d)', icon: Clock,        accent: 'border-amber-300 text-amber-800 bg-amber-50' },
  { key: 'overdue',      label: 'Overdue',      icon: WarningCircle,  accent: 'border-rose-300  text-rose-800  bg-rose-50' },
  { key: 'paid_recent',  label: 'Paid (≤30d)',  icon: Check,          accent: 'border-emerald-300 text-emerald-800 bg-emerald-50' },
];

const AGEING_30_60_90_STYLES = {
  current:  'bg-emerald-100 text-emerald-700 border-emerald-300',
  '0_30':   'bg-amber-100 text-amber-800 border-amber-300',
  '30_60':  'bg-orange-100 text-orange-800 border-orange-300',
  '60_90':  'bg-rose-100 text-rose-700 border-rose-300',
  '90_plus':'bg-rose-200 text-rose-900 border-rose-400',
};

const AGEING_30_60_90_LABEL = {
  current: 'Current',
  '0_30':  '0-30',
  '30_60': '30-60',
  '60_90': '60-90',
  '90_plus': '90+',
};

function formatCurrency(amount, ccy) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy || 'AUD' }).format(amount || 0);
  } catch (_) {
    return `${(amount || 0).toFixed(2)} ${ccy || 'AUD'}`;
  }
}

const TONE_LABEL = {
  friendly: 'Friendly',
  firm: 'Firm',
  final: 'Final notice',
};

const TONE_STYLE = {
  friendly: 'bg-violet-50 border-violet-300 text-violet-700',
  firm: 'bg-orange-50 border-orange-300 text-orange-700',
  final: 'bg-rose-100 border-rose-400 text-rose-800',
};

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function ReminderHistory({ invoiceId, invoiceNumber }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await getInvoiceReminders(invoiceId);
      setItems(r.data?.reminders || []);
    } catch (e) {
      toast.error('Could not load reminder history');
    } finally {
      setLoading(false);
    }
  };
  return (
    <Popover onOpenChange={(o) => o && items === null && load()}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase border bg-zinc-50 border-zinc-300 text-zinc-600 hover:bg-zinc-100"
          data-testid={`reminder-history-${invoiceId}`}
        >
          <Bell size={10} weight="bold" /> log
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="end">
        <div className="text-xs font-bold uppercase tracking-wider mb-2">{invoiceNumber} — reminder log</div>
        {loading ? (
          <div className="text-xs text-zinc-400 italic">Loading…</div>
        ) : !items || items.length === 0 ? (
          <div className="text-xs text-zinc-400 italic">No reminders sent yet.</div>
        ) : (
          <ul className="space-y-2 max-h-64 overflow-y-auto">
            {[...items].reverse().map((r, i) => (
              <li key={i} className="border-l-2 border-violet-300 pl-2 text-[11px]">
                <div className="flex items-center gap-1">
                  <span className={`px-1 py-0.5 text-[9px] font-bold uppercase border ${TONE_STYLE[r.tone] || TONE_STYLE.friendly}`}>
                    {TONE_LABEL[r.tone] || r.tone}
                  </span>
                  <span className="text-zinc-500">{new Date(r.sent_at).toLocaleString()}</span>
                </div>
                <div className="text-zinc-700 truncate">→ {r.recipient}</div>
                <div className="text-zinc-400 capitalize">
                  {r.email_status || 'sent'}{r.pay_url ? ' · pay link attached' : ''}
                </div>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

function Card({ card, onMarkPaid, onMarkUnpaid, onSendReminder, sendingReminder, onToggleSkip }) {
  const due = card.due_date ? new Date(card.due_date).toLocaleDateString() : '—';
  const overdue = card.days_overdue;
  const isPaid = card.payment_status === 'paid';
  const ageingStyle = AGEING_30_60_90_STYLES[card.ageing_30_60_90] || AGEING_30_60_90_STYLES.current;
  const ageingLabel = AGEING_30_60_90_LABEL[card.ageing_30_60_90] || 'Current';
  const reminderCount = card.reminders_count || 0;
  const skipped = !!card.do_not_auto_remind;

  return (
    <div data-testid={`invoice-card-${card.id}`} className="bg-white border border-zinc-200 hover:border-violet-400 p-3 space-y-2 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-sm font-bold truncate">{card.invoice_number}</div>
          {card.customer_email ? (
            <Link
              to={`/invoices/customer/${encodeURIComponent(card.customer_email)}`}
              className="text-xs text-zinc-600 truncate hover:text-violet-700 hover:underline block"
              data-testid={`invoice-customer-link-${card.id}`}
            >
              {card.customer_name}
            </Link>
          ) : (
            <div className="text-xs text-zinc-600 truncate">{card.customer_name}</div>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <div className="font-mono font-bold text-sm">{formatCurrency(card.grand_total, card.currency)}</div>
          {card.order_number && <div className="text-[10px] text-zinc-400 font-mono">{card.order_number}</div>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase border ${ageingStyle}`}>{ageingLabel}</span>
        {!isPaid && overdue !== null && overdue !== undefined && (
          overdue > 0
            ? <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase border bg-rose-50 border-rose-300 text-rose-700">{overdue}d overdue</span>
            : <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase border bg-zinc-50 border-zinc-300 text-zinc-600">{Math.abs(overdue)}d to go</span>
        )}
        {isPaid && card.paid_at && (
          <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase border bg-emerald-50 border-emerald-300 text-emerald-700">
            Paid {new Date(card.paid_at).toLocaleDateString()}
          </span>
        )}
        {!isPaid && reminderCount > 0 && (
          <>
            <span
              className={`px-1.5 py-0.5 text-[10px] font-bold uppercase border ${TONE_STYLE[card.last_reminder_tone] || TONE_STYLE.friendly}`}
              data-testid={`reminder-badge-${card.id}`}
              title={card.last_reminder_at ? `Last sent ${new Date(card.last_reminder_at).toLocaleString()}` : ''}
            >
              <EnvelopeSimple size={10} weight="bold" className="inline mr-0.5" />
              Reminded {reminderCount}× · {relativeTime(card.last_reminder_at)}
            </span>
            <ReminderHistory invoiceId={card.id} invoiceNumber={card.invoice_number} />
          </>
        )}
        {!isPaid && skipped && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase border bg-zinc-100 border-zinc-400 text-zinc-700"
            data-testid={`auto-reminder-skipped-${card.id}`}
            title="Auto-reminder scheduler will skip this customer. Manual reminders still work."
          >
            <BellSlash size={10} weight="bold" /> Skipped
          </span>
        )}
      </div>

      {card.paid_note && (
        <div className="text-[11px] text-zinc-500 italic truncate">"{card.paid_note}"</div>
      )}

      <div className="flex items-center justify-between text-[11px] text-zinc-500">
        <div className="flex items-center gap-1"><Calendar size={11} /> Due {due}</div>
      </div>

      <div className="flex flex-wrap gap-1 pt-1 border-t border-zinc-100">
        <a
          href={invoicePdfUrl(card.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 min-w-[60px] inline-flex items-center justify-center gap-1 px-2 py-1 border border-zinc-300 text-[11px] font-bold uppercase tracking-wider hover:bg-zinc-50"
          data-testid={`invoice-pdf-${card.id}`}
        >
          <FileText size={12} /> PDF
        </a>
        {!isPaid && (
          <button
            onClick={() => onToggleSkip(card)}
            className={`inline-flex items-center justify-center px-2 py-1 border text-[11px] font-bold uppercase tracking-wider ${
              skipped
                ? 'border-zinc-400 bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                : 'border-zinc-300 hover:bg-zinc-50 text-zinc-500'
            }`}
            data-testid={`invoice-toggle-skip-${card.id}`}
            title={skipped ? 'Currently excluded from auto-reminders — click to re-include' : 'Skip auto-reminders for this customer'}
          >
            {skipped ? <BellSlash size={12} weight="bold" /> : <Bell size={12} />}
          </button>
        )}
        {!isPaid && (
          <button
            onClick={() => onSendReminder(card)}
            disabled={sendingReminder}
            className="flex-1 min-w-[90px] inline-flex items-center justify-center gap-1 px-2 py-1 border border-violet-500 bg-violet-50 text-violet-700 text-[11px] font-bold uppercase tracking-wider hover:bg-violet-100 disabled:opacity-50"
            data-testid={`invoice-send-reminder-${card.id}`}
            title={!card.customer_email ? 'No customer email on file' : 'Send a payment reminder with a Stripe pay link'}
          >
            <EnvelopeSimple size={12} weight="bold" /> {reminderCount > 0 ? 'Resend' : 'Remind'}
          </button>
        )}
        {!isPaid ? (
          <button
            onClick={() => onMarkPaid(card)}
            className="flex-1 min-w-[80px] inline-flex items-center justify-center gap-1 px-2 py-1 border border-emerald-500 bg-emerald-50 text-emerald-700 text-[11px] font-bold uppercase tracking-wider hover:bg-emerald-100"
            data-testid={`invoice-mark-paid-${card.id}`}
          >
            <Check size={12} weight="bold" /> Mark paid
          </button>
        ) : (
          <button
            onClick={() => onMarkUnpaid(card)}
            className="flex-1 min-w-[60px] inline-flex items-center justify-center gap-1 px-2 py-1 border border-zinc-300 text-[11px] font-bold uppercase tracking-wider hover:bg-zinc-50"
            data-testid={`invoice-mark-unpaid-${card.id}`}
          >
            <ArrowsCounterClockwise size={12} /> Unpay
          </button>
        )}
      </div>
    </div>
  );
}

export default function OutstandingInvoices() {
  const [data, setData] = useState({ columns: { open: [], due_soon: [], overdue: [], paid_recent: [] }, totals: {}, currency: 'AUD' });
  const [loading, setLoading] = useState(true);
  const [paidDialog, setPaidDialog] = useState(null); // {card}
  const [paidAt, setPaidAt] = useState('');
  const [paidNote, setPaidNote] = useState('');
  const [sendingReminder, setSendingReminder] = useState(null); // invoice id while in flight
  const [unpaidConfirm, setUnpaidConfirm] = useState(null); // {card}
  const [cooldownOverride, setCooldownOverride] = useState(null); // {card, message}

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await getOutstandingKanban();
      setData(r.data || { columns: { open: [], due_soon: [], overdue: [], paid_recent: [] }, totals: {}, currency: 'AUD' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load outstanding invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openMarkPaid = (card) => {
    setPaidDialog({ card });
    setPaidAt(new Date().toISOString().slice(0, 10));
    setPaidNote('');
  };

  const confirmMarkPaid = async () => {
    if (!paidDialog) return;
    try {
      await markInvoicePaid(paidDialog.card.id, { paid_at: paidAt, note: paidNote });
      toast.success(`${paidDialog.card.invoice_number} marked paid`);
      setPaidDialog(null);
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not mark paid');
    }
  };

  const handleMarkUnpaid = (card) => setUnpaidConfirm({ card });

  const confirmMarkUnpaid = async () => {
    const card = unpaidConfirm?.card;
    if (!card) return;
    try {
      await markInvoiceUnpaid(card.id);
      toast.success(`${card.invoice_number} reverted to unpaid`);
      setUnpaidConfirm(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not unpay');
    }
  };

  const handleSendReminder = async (card, opts = {}) => {
    if (!card.customer_email) {
      toast.error('No customer email on this invoice — add one and try again');
      return;
    }
    setSendingReminder(card.id);
    try {
      const r = await sendInvoiceReminder(card.id, {
        origin_url: window.location.origin,
        override_cooldown: !!opts.override,
      });
      const tone = r.data?.tone || 'friendly';
      const status = r.data?.email_status || 'sent';
      const tail = r.data?.pay_url ? ' · pay link attached' : '';
      toast.success(`Reminder (${tone}) → ${card.customer_email} [${status}]${tail}`);
      await refresh();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        // Cooldown — surface the AlertDialog so the operator can choose to override
        setCooldownOverride({ card, message: typeof detail === 'string' ? detail : 'Reminder cooldown hit.' });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not send reminder');
      }
    } finally {
      setSendingReminder(null);
    }
  };

  const confirmCooldownOverride = async () => {
    const card = cooldownOverride?.card;
    setCooldownOverride(null);
    if (card) await handleSendReminder(card, { override: true });
  };

  const handleToggleSkip = async (card) => {
    try {
      const next = !card.do_not_auto_remind;
      await setInvoiceSkipAutoReminders(card.id, next);
      toast.success(
        next
          ? `${card.customer_name} excluded from auto-reminders. Manual reminders still work.`
          : `${card.customer_name} re-included in auto-reminders.`
      );
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update skip flag');
    }
  };

  return (
    <Layout>
      <div data-testid="outstanding-invoices-page" className="p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CurrencyDollar size={28} weight="bold" className="text-violet-600" />
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Outstanding invoices</h1>
              <p className="text-muted-foreground">Issued invoices, grouped by ageing. Mark paid to clear them.</p>
            </div>
          </div>
          <Button onClick={refresh} variant="outline" data-testid="refresh-kanban">
            <ArrowsCounterClockwise size={14} className="mr-1" /> Refresh
          </Button>
        </div>

        <ArEffectivenessStrip />

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {COLUMN_DEFS.map((col) => {
            const Icon = col.icon;
            const cards = data.columns[col.key] || [];
            const total = data.totals[col.key] || 0;
            return (
              <div key={col.key} data-testid={`kanban-col-${col.key}`} className="space-y-3">
                <div className={`flex items-center justify-between border-2 ${col.accent} px-3 py-2`}>
                  <div className="flex items-center gap-2">
                    <Icon size={16} weight="bold" />
                    <span className="font-bold uppercase text-xs tracking-widest">{col.label}</span>
                    <span className="font-mono text-[11px] text-zinc-500">({cards.length})</span>
                  </div>
                  <span className="font-mono font-bold text-xs">{formatCurrency(total, data.currency)}</span>
                </div>
                <div className="space-y-2 min-h-[80px]">
                  {loading ? (
                    <div className="text-center text-zinc-400 text-sm italic py-6">Loading…</div>
                  ) : cards.length === 0 ? (
                    <div className="text-center text-zinc-400 text-sm italic py-6">Nothing here.</div>
                  ) : (
                    cards.map((card) => (
                      <Card key={card.id} card={card} onMarkPaid={openMarkPaid} onMarkUnpaid={handleMarkUnpaid} onSendReminder={handleSendReminder} sendingReminder={sendingReminder === card.id} onToggleSkip={handleToggleSkip} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!paidDialog} onOpenChange={(o) => !o && setPaidDialog(null)}>
        <DialogContent data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Mark {paidDialog?.card?.invoice_number} as paid</DialogTitle>
            <DialogDescription>
              Customer: <strong>{paidDialog?.card?.customer_name}</strong> · {formatCurrency(paidDialog?.card?.grand_total, paidDialog?.card?.currency)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Payment date</Label>
              <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} data-testid="mark-paid-date" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input
                value={paidNote}
                onChange={(e) => setPaidNote(e.target.value)}
                placeholder="Bank transfer ref / cheque # / cash receipt"
                data-testid="mark-paid-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaidDialog(null)} data-testid="mark-paid-cancel">Cancel</Button>
            <Button onClick={confirmMarkPaid} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mark-paid-confirm">
              <Check size={14} weight="bold" className="mr-1" /> Confirm payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!unpaidConfirm} onOpenChange={(o) => !o && setUnpaidConfirm(null)}>
        <AlertDialogContent data-testid="mark-unpaid-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revert {unpaidConfirm?.card?.invoice_number} to unpaid?</AlertDialogTitle>
            <AlertDialogDescription>
              This will move <strong>{unpaidConfirm?.card?.customer_name}</strong>'s
              {' '}{formatCurrency(unpaidConfirm?.card?.grand_total, unpaidConfirm?.card?.currency)} back to its outstanding column.
              The original payment date and note will be cleared.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mark-unpaid-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmMarkUnpaid}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="mark-unpaid-confirm"
            >
              Yes, revert to unpaid
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!cooldownOverride} onOpenChange={(o) => !o && setCooldownOverride(null)}>
        <AlertDialogContent data-testid="cooldown-override-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Already reminded recently</AlertDialogTitle>
            <AlertDialogDescription>
              {cooldownOverride?.message} Force-sending will email
              {' '}<strong>{cooldownOverride?.card?.customer_email}</strong> again right now —
              this can feel spammy if your customer just received a reminder.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cooldown-override-cancel">Wait</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCooldownOverride}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="cooldown-override-confirm"
            >
              Force send anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
